/** Validates the staff assignment */
import {CalendarEntry, StaffAssignment, StaffLink, Venue} from "../../../../../../../../../api/grs";
import {FormErrors, StaffAssignmentFormError} from "../../../../../../../../../utils/formUtils";
import {EMAIL_REGEX} from "../../../../../../../../../utils/regexUtils";
import store from "../../../../../../../../../store/Store";
import OverlapWarning from "../../../../../../../../Form/OverlapWarning";
import {ClinicalGrade} from "../../../../../../../../../api/staff";

export function validateRate(rate?: number): FormErrors {
    const messages: string[] = [];

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (rate < 0) messages.push("Rate cannot be less than 0");

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (rate === 0) messages.push("Rate cannot be 0");

    return {
        messages,
        valid: messages.length === 0
    };
}

export function validateStaffAssignment(assignment: StaffAssignment): FormErrors {
    const messages: string[] = [];

    if (!assignment.staffMember) {
        messages.push("Staff member must be selected");
    }
    if (assignment.staffMember?.staffId.length === 0) {
        messages.push("Staff member must be selected");
    }

    if (assignment.staffMember && assignment.staffMember.staffId.length === 0) {
        messages.push("Staff member cannot be blank");
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (assignment.startDate >= 0 && assignment.endDate >= 0) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (assignment.endDate < assignment.startDate) {
            messages.push("End time can't be earlier than the start time");
        }
        if (assignment.startDate === 0) messages.push("Pick a start time");
        if (assignment.endDate === 0) messages.push("Pick an end time");
    }

    const rateErrors = validateRate(assignment.rate);
    const allMessages = messages.concat(rateErrors.messages);

    return {
        messages: allMessages,
        valid: allMessages.length === 0
    };
}

export function validateExternalAssignment(assignment: StaffAssignment): FormErrors {
    const messages: string[] = [];
    const {staffMember} = assignment;

    if (!staffMember) {
        messages.push("Staff member must be selected");
    }

    if (staffMember && staffMember.external) {
        if (!EMAIL_REGEX.test(staffMember.staffId)) {
            messages.push("Email for external staff member is not valid");
        }

        if (staffMember.staffName.length < 3) {
            messages.push("External staff name is not valid");
        }
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (assignment.startDate >= 0 && assignment.endDate >= 0) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (assignment.endDate < assignment.startDate) {
            messages.push("End time can't be earlier than the start time");
        }

        if (assignment.startDate === 0) {
            messages.push("Pick a start time");
        }

        if (assignment.endDate === 0) {
            messages.push("Pick an end time");
        }
    }

    const rateErrors = validateRate(assignment.rate);
    const allMessages = messages.concat(rateErrors.messages);

    return {
        messages: allMessages,
        valid: allMessages.length === 0
    };
}

/** Checks the list of overlapping entries */

export async function checkIfUserHasOverlappingEntries(
    overlappingEntries: CalendarEntry[],
    staff: StaffLink,
    currentEntry: CalendarEntry
): Promise<StaffAssignmentFormError> {
    if (overlappingEntries.length === 0) {
        return {
            valid: true,
            message: "",
            canBeAssigned: true
        };
    }

    /** Venues should be loaded here, having to null check because of typescript */
    const venues = store.getState().venueList.data;
    if (!venues) {
        return {
            valid: true,
            message: "",
            canBeAssigned: true
        };
    }

    const startDate = currentEntry.startDate;
    const endDate = currentEntry.endDate;
    let allowable = true;

    // Iterate through all entries, if any do overlap, return true and emit error to error store.
    for (const entry of overlappingEntries) {
        if (endDate > entry.startDate && endDate < entry.endDate) {
            allowable = false;
        }
        if (startDate > entry.startDate && startDate < entry.endDate) {
            allowable = false;
        }
    }

    const overlapProps = {
        overlappingEvents: overlappingEntries,
        staff: staff,
        currentEvent: currentEntry,
        allowable: allowable
    };
    const message = OverlapWarning(overlapProps);

    return {
        valid: false,
        message: message,
        canBeAssigned: allowable
    };
}

/** Determines whether the values input into the Rate field are valid  */
export function validateRateInput(value: string): boolean {
    const regex = /^[+-]?((\d+(\.\d*)?)|(\.\d))$/;
    if (value.length > 0) {
        // Not a decimal value
        if (!regex.test(value)) return false;

        // Decimal place -> greater than 2 places
        if (countDecimals(+value) > 2) return false;
    }
    return true;
}

function countDecimals(value: number) {
    if (Math.floor(value) === value) return 0;
    return value.toString().split(".")[1].length || 0;
}

export function canUserEditRate(
    venue: Venue | null | undefined,
    clinicalGrade: ClinicalGrade | undefined
): boolean {
    if (!clinicalGrade) return true;
    if (!venue?.clinicalGradeRates) return true;
    if (!venue.clinicalGradeRates[clinicalGrade]) return true;

    return false;
}
